"use client";

import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, User, ChevronRight, ChevronLeft } from "lucide-react";

const TESTIMONIALS = [
  {
    id: 1,
    name: "Alisa Fox",
    role: "Founder",
    image: "/images/testimonial/testimonial-3-1.jpg",
    text: "I have been using IK Travel for my car rental needs for over 5 years now. I have never had any problems with their service. Their customer support is always responsive and helpful. I would recommend IK Travel to anyone looking for a reliable car rental provider.",
  },
  {
    id: 2,
    name: "Robert Son",
    role: "Founder",
    image: "/images/testimonial/testimonial-3-2.jpg",
    text: "We have been using IK Travel for our trips needs for several years now and have always been happy with their service. Their customer support is excellent and they are always available to help with any issues we have. Their prices are also very competitive.",
  },
  {
    id: 3,
    name: "Danil Aarth",
    role: "Founder",
    image: "/images/testimonial/testimonial-3-3.jpg",
    text: "Endorsed by industry experts, IK Travel is the car rental solution you can trust. With years of experience in the field, we provide fast, reliable and secure car rental services. I highly recommend IK Travel for all your self-drive needs.",
  },
];

function TestimonialCard({
  testimonial,
  index,
}: {
  testimonial: (typeof TESTIMONIALS)[0];
  index: number;
}) {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1], delay: index * 0.05 }}
      className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_85%] md:flex-[0_0_80%] lg:flex-[0_0_85%]"
    >
      <div className="group relative rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 p-6 md:p-8">
        <div className="flex flex-col sm:flex-row gap-6 md:gap-8">
          <div className="flex-shrink-0 flex flex-col items-center sm:items-start">
            <div className="relative h-20 w-20 rounded-full overflow-hidden bg-gradient-to-br from-neutral-200 to-neutral-300 dark:from-neutral-700 dark:to-neutral-800">
              {!imgError ? (
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="h-full w-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <User className="w-10 h-10 text-neutral-400 dark:text-neutral-500" aria-hidden />
                </div>
              )}
            </div>
            <div className="flex gap-0.5 mt-2" aria-label={`${testimonial.name} rated 5 stars`}>
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-[#FFB51D] text-[#FFB51D]"
                  aria-hidden
                />
              ))}
            </div>
            <div className="mt-2 text-center sm:text-left">
              <h4 className="font-bold text-[#131222] dark:text-white group-hover:text-[#FFB51D] transition-colors">
                {testimonial.name}
              </h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {testimonial.role}
              </p>
            </div>
          </div>
          <div className="flex-1">
            <Quote
              className="w-10 h-10 text-[#FFB51D]/30 dark:text-[#FFB51D]/20 mb-2"
              aria-hidden
            />
            <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
              {testimonial.text}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function IKTestimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onInit);
  }, [emblaApi, onInit, onSelect]);

  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="testimonials-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left - Section title */}
          <div className="lg:col-span-5 mb-2 lg:mb-0">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 mb-4">
                <span className="text-sm font-medium text-[#131222] dark:text-[#FFB51D]">
                  Our Testimonial
                </span>
              </div>
              <h2
                id="testimonials-heading"
                className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white"
              >
                What Peoples Say{" "}
                <br className="hidden lg:block" />
                about IK Travel
              </h2>
            </motion.div>
          </div>

          {/* Right - Carousel */}
          <div className="lg:col-span-7 relative">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex gap-6 touch-pan-y">
                {TESTIMONIALS.map((testimonial, index) => (
                  <TestimonialCard
                    key={testimonial.id}
                    testimonial={testimonial}
                    index={index}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 gap-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={scrollPrev}
                  disabled={prevBtnDisabled}
                  className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="w-6 h-6" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={scrollNext}
                  disabled={nextBtnDisabled}
                  className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="w-6 h-6" aria-hidden />
                </button>
              </div>

              {scrollSnaps.length > 1 && (
                <div
                  className="flex gap-2"
                  role="tablist"
                  aria-label="Testimonial carousel dots"
                >
                  {scrollSnaps.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => scrollTo(index)}
                      role="tab"
                      aria-selected={index === selectedIndex}
                      aria-label={`Go to testimonial ${index + 1}`}
                      className="group min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full"
                    >
                      <span
                        className={`block rounded-full transition-all ${
                          index === selectedIndex
                            ? "w-8 h-2 bg-[#FFB51D]"
                            : "w-2 h-2 bg-[#131222]/50 group-hover:bg-[#131222]/70"
                        }`}
                        aria-hidden
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}
